import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useApp, API } from "@/context/AppContext";
import { Headphones, Send, X, Loader2, Phone, Mail, CalendarCheck, Check, ShoppingBag } from "lucide-react";

/** Persistent per-browser session id so the backend can keep chat context. */
function useSessionId() {
  const [sid] = useState(() => {
    let s = localStorage.getItem("ct01_basra_sid");
    if (!s) { s = `basra-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`; localStorage.setItem("ct01_basra_sid", s); }
    return s;
  });
  return sid;
}

const STATUS_LABEL = {
  de: "Online · Ihre digitale Assistentin", en: "Online · Your digital assistant",
  fr: "En ligne · Votre assistante digitale", it: "Online · La tua assistente digitale",
  ar: "متصلة · مساعدتك الرقمية", es: "En línea · Tu asistente digital",
  pt: "Online · A sua assistente digital", tr: "Çevrimiçi · Dijital asistanınız",
  nl: "Online · Jouw digitale assistente", sv: "Online · Din digitala assistent",
  hr: "Na vezi · Vaša digitalna asistentica", sr: "Na vezi · Vaša digitalna asistentkinja",
  bs: "Online · Vaša digitalna asistentica", sq: "Në linjë · Asistentja juaj digjitale",
  fa: "آنلاین · دستیار دیجیتال شما", ta: "இணைப்பில் · உங்கள் டிஜிட்டல் உதவியாளர்",
};
const INPUT_HINT = {
  de: "Schreiben Sie eine Nachricht …", en: "Write a message …", fr: "Écrivez un message …", it: "Scrivi un messaggio …",
  ar: "اكتب رسالة …", es: "Escribe un mensaje …", pt: "Escreva uma mensagem …", tr: "Bir mesaj yazın …",
  nl: "Schrijf een bericht …", sv: "Skriv ett meddelande …", hr: "Napišite poruku …", sr: "Napišite poruku …",
  bs: "Napišite poruku …", sq: "Shkruani një mesazh …", fa: "پیامی بنویسید …", ta: "செய்தி எழுதுங்கள் …",
};
const CONTACT_LABEL = {
  de: "Direkt kontaktieren", en: "Contact us directly", fr: "Nous contacter", it: "Contattaci",
  ar: "تواصل مباشرة معنا", es: "Contáctanos", pt: "Contacte-nos", tr: "Doğrudan iletişime geç",
  nl: "Direct contact", sv: "Kontakta oss", hr: "Kontaktirajte nas", sr: "Kontaktirajte nas",
  bs: "Kontaktirajte nas", sq: "Na kontaktoni", fa: "تماس مستقیم", ta: "எங்களை தொடர்பு கொள்ளுங்கள்",
};
const ADDED_LABEL = {
  de: "Zur Buchung hinzugefügt", en: "Added to your booking", fr: "Ajouté à votre réservation",
  it: "Aggiunto alla prenotazione", ar: "تمت الإضافة إلى الحجز", es: "Añadido a tu reserva",
  pt: "Adicionado à sua reserva", tr: "Rezervasyona eklendi", nl: "Toegevoegd aan boeking",
  sv: "Tillagd i din bokning", hr: "Dodano u rezervaciju", sr: "Dodato u rezervaciju",
  bs: "Dodano u rezervaciju", sq: "U shtua në rezervim", fa: "به رزرو اضافه شد", ta: "முன்பதிவில் சேர்க்கப்பட்டது",
};
const ADDED_PRODUCT_LABEL = {
  de: "Zum Warenkorb hinzugefügt", en: "Added to your cart", fr: "Ajouté au panier",
  it: "Aggiunto al carrello", ar: "تمت الإضافة إلى السلة", es: "Añadido al carrito",
  pt: "Adicionado ao carrinho", tr: "Sepete eklendi", nl: "Toegevoegd aan winkelmandje",
  sv: "Tillagd i varukorgen", hr: "Dodano u košaricu", sr: "Dodato u korpu",
  bs: "Dodato u korpu", sq: "U shtua në shportë", fa: "به سبد افزوده شد", ta: "கூடையில் சேர்க்கப்பட்டது",
};

export default function Basra() {
  const {
    lang, isRTL, user, bookingCustomer, t,
    addBookingItem, removeBookingItem, bookingCart,
    cart, addToCart, removeFromCart, updateQty,
  } = useApp();
  const sid = useSessionId();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);           // {role, content, suggested_booking?, suggested_product?, cart_action?, added?}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [greetedForLang, setGreetedForLang] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Best-effort customer name for personalization: logged-in profile first, then whatever
  // they typed at the booking-customer step, then null (guest → polite generic address).
  const customerName = (user?.first_name || user?.name || bookingCustomer?.first_name || "").trim() || null;

  // Restore prior conversation from the backend the very first time Basra is
  // opened in this browser session. This survives page reloads, tab switches
  // and even a different device (same session id in localStorage) so the
  // customer never sees Basra "forget who they are".
  useEffect(() => {
    if (!open || historyLoaded) return;
    axios.get(`${API}/basra/history`, { params: { session_id: sid } })
      .then(r => {
        const restored = Array.isArray(r.data?.messages) ? r.data.messages : [];
        if (restored.length > 0) {
          // A real conversation exists → skip the greeting entirely so Basra
          // doesn't re-introduce herself, and don't ask for a fresh greeting.
          setMessages(restored);
          setGreetedForLang(lang);
        }
        setHistoryLoaded(true);
      })
      .catch(() => { setHistoryLoaded(true); });
  }, [open, sid, historyLoaded, lang]);

  // Fetch a localized greeting when the widget opens or the language changes,
  // BUT only for a fresh conversation (no prior messages restored from server).
  useEffect(() => {
    if (!open || !historyLoaded) return;
    if (greetedForLang === lang) return;
    if (messages.length > 0) { setGreetedForLang(lang); return; }
    axios.get(`${API}/basra/greeting`, { params: { lang } })
      .then(r => {
        setMessages([{ role: "assistant", content: r.data.greeting, isGreeting: true }]);
        setGreetedForLang(lang);
      })
      .catch(() => {
        setMessages([{ role: "assistant", content: "Hello 😊 I'm Basra.", isGreeting: true }]);
        setGreetedForLang(lang);
      });
  }, [open, lang, greetedForLang, historyLoaded, messages.length]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, busy]);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  // Build a compact cart snapshot for the backend — just the fields the LLM needs to
  // resolve pronouns like "this" and to emit REMOVE/QTY tags with the right IDs.
  const snapshotCart = () => ({
    cart_products: cart.map(i => ({
      product_id: i.product_id, slug: i.slug,
      name: (typeof i.name === "string") ? i.name : (i.name?.[lang] || i.name?.en || i.name?.de || i.slug),
      quantity: i.quantity, size: i.size || null, variant: i.variant || null,
    })),
    cart_services: bookingCart.map(b => ({
      uid: b.uid, service_id: b.service_id, name: b.name,
      variant: b.variant || null, variant_label: b.variant_label || null,
    })),
  });

  // Apply the cart mutation the LLM emitted. Returns a localized "done" label
  // (already provided by the backend) so the caller can render a confirmation chip.
  const applyCartAction = (action) => {
    if (!action || !action.op) return null;
    if (action.op === "remove_service") {
      if (!action.uid) return null;
      removeBookingItem(action.uid);
    } else if (action.op === "remove_product") {
      if (!action.product_id) return null;
      removeFromCart(action.product_id, action.size, action.variant);
    } else if (action.op === "set_qty") {
      if (!action.product_id || !action.quantity) return null;
      updateQty(action.product_id, action.size, action.variant, action.quantity);
    } else {
      return null;
    }
    return action.label || "Done";
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");
    setBusy(true);
    try {
      const history = nextMessages
        .filter(m => !m.isGreeting)
        .map(m => ({ role: m.role, content: m.content }));
      const snap = snapshotCart();
      const authToken = localStorage.getItem("ct01_token");
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const { data } = await axios.post(`${API}/basra/chat`, {
        session_id: sid, message: text, language: lang, customer_name: customerName,
        history: history.slice(-12), ...snap,
      }, { headers });
      const appliedLabel = data.cart_action ? applyCartAction(data.cart_action) : null;
      setMessages(m => [...m, {
        role: "assistant",
        content: data.reply,
        suggested_booking: data.suggested_booking || null,
        suggested_product: data.suggested_product || null,
        applied_label: appliedLabel,
      }]);
    } catch (e) {
      setMessages(m => [...m, {
        role: "assistant",
        content: e.response?.data?.detail || "😊 …",
        isFallback: true,
      }]);
    } finally { setBusy(false); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  const acceptBooking = (msgIdx, suggestion) => {
    if (!suggestion || !suggestion.service_id) return;
    const already = bookingCart.some(b =>
      b.service_id === suggestion.service_id &&
      (b.variant || null) === (suggestion.variant || null)
    );
    if (!already) {
      addBookingItem({
        service_id: suggestion.service_id, name: suggestion.name,
        variant: suggestion.variant || null, variant_label: suggestion.variant_label || null,
        price_chf: Number(suggestion.price_chf), duration_min: Number(suggestion.duration_min),
        is_tattoo: false,
      });
    }
    setMessages(prev => prev.map((m, i) => (i === msgIdx ? { ...m, added: true } : m)));
    setOpen(false);
    setTimeout(() => nav("/booking/schedule"), 120);
  };

  const acceptProduct = (msgIdx, suggestion) => {
    if (!suggestion || !suggestion.product_id) return;
    // Reuse the shared addToCart helper so quantity merging + toast/telemetry all work.
    // Note: addToCart opens the side drawer — we're OK with that as it gives the customer
    // instant visual feedback that the item landed in the cart.
    addToCart(
      {
        id: suggestion.product_id,
        slug: suggestion.slug,
        name: suggestion.name,
        price_chf: suggestion.price_chf,
        sale_price_chf: null,
        images: suggestion.image ? [suggestion.image] : [],
      },
      1,
      suggestion.size || null,
      null,
    );
    setMessages(prev => prev.map((m, i) => (i === msgIdx ? { ...m, added_product: true } : m)));
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label={t("aria_basra")}
        data-testid="basra-toggle"
        className={`fixed bottom-6 ${isRTL ? "left-6" : "right-6"} z-40 group inline-flex items-center gap-2 pl-3 pr-4 py-3 bg-gold text-black rounded-full shadow-2xl hover:scale-105 transition-transform`}
        style={{ display: open ? "none" : "inline-flex" }}
      >
        <span className="relative flex items-center justify-center w-8 h-8 rounded-full bg-black">
          <Headphones size={16} className="text-gold" />
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-gold animate-pulse" />
        </span>
        <span className="text-[11px] uppercase tracking-[0.2em] font-medium">Basra</span>
      </button>

      {open && (
        <div
          className={`fixed z-50 bottom-0 ${isRTL ? "left-0 sm:left-6" : "right-0 sm:right-6"} sm:bottom-6 w-full sm:w-[380px] h-[92vh] sm:h-[560px] bg-[#0F0F0F] border border-gold/30 sm:rounded-lg shadow-2xl flex flex-col animate-in fade-in slide-in-from-bottom-4`}
          data-testid="basra-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-black to-[#1a1a1a]">
            <div className="flex items-center gap-3">
              <div className="relative w-9 h-9 rounded-full bg-gold flex items-center justify-center">
                <Headphones size={16} className="text-black" />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-[#0F0F0F]" />
              </div>
              <div>
                <div className="text-white text-sm font-medium">Basra</div>
                <div className="text-[10px] text-white/50">{STATUS_LABEL[lang] || STATUS_LABEL.en}</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white" aria-label={t("aria_close")} data-testid="basra-close">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scroll-smooth" data-testid="basra-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-gold text-black rounded-2xl rounded-br-md"
                    : "bg-[#1a1a1a] text-white/90 rounded-2xl rounded-bl-md border border-white/5"
                }`}>{m.content}</div>
                {m.role === "assistant" && m.suggested_booking && (
                  <BookingCTA lang={lang} suggestion={m.suggested_booking} added={m.added}
                    onClick={() => acceptBooking(i, m.suggested_booking)} />
                )}
                {m.role === "assistant" && m.suggested_product && (
                  <ProductCard lang={lang} suggestion={m.suggested_product} added={m.added_product}
                    onClick={() => acceptProduct(i, m.suggested_product)} />
                )}
                {m.role === "assistant" && m.applied_label && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-[11px]" data-testid="basra-cart-action">
                    <Check size={12} /> {m.applied_label}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-[#1a1a1a] rounded-2xl rounded-bl-md px-4 py-3 border border-white/5 flex gap-1.5">
                  <span className="w-1.5 h-1.5 bg-gold/60 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-gold/60 rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="w-1.5 h-1.5 bg-gold/60 rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            )}
          </div>

          <div className="px-4 py-2 border-t border-white/5 bg-black/40">
            <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1.5">{CONTACT_LABEL[lang] || CONTACT_LABEL.en}</div>
            <div className="flex gap-2 text-[11px]">
              <a href="tel:+41782021272" className="flex-1 inline-flex items-center justify-center gap-1.5 border border-white/10 hover:border-gold/40 text-white/70 hover:text-gold py-1.5 rounded" data-testid="basra-phone">
                <Phone size={11} /> +41 78 202 12 72
              </a>
              <a href="mailto:info@01co.ch" className="flex-1 inline-flex items-center justify-center gap-1.5 border border-white/10 hover:border-gold/40 text-white/70 hover:text-gold py-1.5 rounded" data-testid="basra-email">
                <Mail size={11} /> info@01co.ch
              </a>
            </div>
          </div>

          <div className="p-3 border-t border-white/10 bg-[#0F0F0F]">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef} rows={1} value={input}
                onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
                placeholder={INPUT_HINT[lang] || INPUT_HINT.en}
                className="flex-1 resize-none bg-[#1a1a1a] border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold focus:outline-none max-h-28"
                data-testid="basra-input"
              />
              <button onClick={send} disabled={!input.trim() || busy}
                className="shrink-0 w-10 h-10 rounded-full bg-gold text-black inline-flex items-center justify-center disabled:opacity-30 hover:scale-105 transition-transform"
                data-testid="basra-send" aria-label={t("aria_send")}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function BookingCTA({ lang, suggestion, added, onClick }) {
  const label = suggestion.button_label || "Book this service";
  const summary = `${suggestion.name}${suggestion.variant_label ? ` — ${suggestion.variant_label}` : ""} · ${suggestion.duration_min} min · CHF ${Number(suggestion.price_chf).toFixed(0)}`;
  if (added) {
    return (
      <div className="mt-2 max-w-[85%] inline-flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs" data-testid="basra-booking-added">
        <Check size={13} /> {ADDED_LABEL[lang] || ADDED_LABEL.en}
      </div>
    );
  }
  return (
    <button type="button" onClick={onClick}
      data-testid={`basra-book-btn-${suggestion.service_id}`}
      className="mt-2 max-w-[90%] group inline-flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-gold text-black shadow hover:scale-[1.02] active:scale-[0.99] transition-transform">
      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-gold">
        <CalendarCheck size={13} />
      </span>
      <span className="flex flex-col items-start leading-tight text-left">
        <span className="text-[11px] uppercase tracking-[0.15em] font-medium">{label}</span>
        <span className="text-[10px] text-black/70 font-normal">{summary}</span>
      </span>
    </button>
  );
}

function ProductCard({ lang, suggestion, added, onClick }) {
  const label = suggestion.button_label || "Add to cart";
  return (
    <div className="mt-2 max-w-[90%] border border-white/10 bg-[#181818] rounded-lg overflow-hidden" data-testid={`basra-product-card-${suggestion.product_id}`}>
      <div className="flex">
        {suggestion.image ? (
          <img src={suggestion.image} alt="" className="w-24 h-24 object-cover shrink-0" />
        ) : (
          <div className="w-24 h-24 shrink-0 bg-white/5 flex items-center justify-center text-white/30"><ShoppingBag size={22} /></div>
        )}
        <div className="p-3 flex-1 min-w-0">
          <div className="text-white text-sm font-medium leading-snug truncate">{suggestion.name}</div>
          {suggestion.size && <div className="text-white/50 text-[11px] mt-0.5">{suggestion.size}</div>}
          {suggestion.description && (
            <div className="text-white/60 text-[11px] mt-1 leading-relaxed line-clamp-2">{suggestion.description}</div>
          )}
          <div className="text-gold text-sm font-serif-display mt-1">CHF {Number(suggestion.price_chf).toFixed(2)}</div>
        </div>
      </div>
      {added ? (
        <div className="px-3 py-2 bg-emerald-500/10 border-t border-emerald-500/40 text-emerald-300 text-[11px] inline-flex items-center gap-2 w-full" data-testid="basra-product-added">
          <Check size={13} /> {ADDED_PRODUCT_LABEL[lang] || ADDED_PRODUCT_LABEL.en}
        </div>
      ) : (
        <button type="button" onClick={onClick}
          data-testid={`basra-buy-btn-${suggestion.product_id}`}
          className="w-full px-3 py-2.5 bg-gold text-black inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-gold/90 transition-colors">
          <ShoppingBag size={13} /> {label}
        </button>
      )}
    </div>
  );
}
